import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useAgents } from '../../hooks/useAgents';
import { Skeleton } from '../ui/Skeleton';
import { 
  Search, 
  MessageSquare, 
  BarChart3, 
  Target, 
  Users, 
  PenTool,
  TrendingUp,
  Clock,
  Trash2
} from 'lucide-react';

interface ConversationHistoryProps {
  agentType: string;
  currentConversationId?: string;
  onConversationSelect: (id: string) => void;
}

const agentIcons = {
  data: BarChart3,
  marketing: Target,
  strategy: TrendingUp,
  persona: Users,
  content: PenTool
};

const agentColors = {
  data: 'bg-blue-100 text-blue-600',
  marketing: 'bg-green-100 text-green-600',
  strategy: 'bg-purple-100 text-purple-600',
  persona: 'bg-pink-100 text-pink-600',
  content: 'bg-orange-100 text-orange-600'
};

export function ConversationHistory({ agentType, currentConversationId, onConversationSelect }: ConversationHistoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const { useConversations, deleteConversation } = useAgents();
  
  const { data, isLoading } = useConversations({
    agentType: agentType === 'all' ? undefined : agentType
  });
  
  const conversations = data?.conversations || [];

  const filteredConversations = conversations.filter(conv => 
    (agentType === 'all' || conv.agentType === agentType) &&
    (searchQuery === '' || conv.title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now.getTime() - time.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  const handleClearHistory = async () => {
    if (window.confirm('Are you sure you want to clear all conversations? This cannot be undone.')) {
      try {
        // Delete each conversation
        for (const conv of conversations) {
          await deleteConversation(conv.id);
        }
      } catch (error) {
        console.error('Failed to clear history:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="p-3 border-b border-gray-200">
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="flex-1 p-2 space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search - Mobile Optimized */}
      <div className="p-3 sm:p-4 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-10 sm:h-9 text-base sm:text-sm rounded-lg border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
          />
        </div>
      </div>

      {/* Conversations List - Enhanced Mobile */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length > 0 ? (
          <div className="space-y-1 p-2">
            {filteredConversations.map((conversation) => {
              const Icon = agentIcons[conversation.agentType as keyof typeof agentIcons] || MessageSquare;
              const colorClass = agentColors[conversation.agentType as keyof typeof agentColors] || 'bg-gray-100 text-gray-600';
              const isActive = conversation.id === currentConversationId;
              
              return (
                <div
                  key={conversation.id}
                  className={`flex items-start p-3 sm:p-2 rounded-xl sm:rounded-lg cursor-pointer transition-all duration-200 ${
                    isActive 
                      ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' 
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:shadow-sm'
                  }`}
                  onClick={() => onConversationSelect(conversation.id)}
                >
                  <div className={`w-10 h-10 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                    <Icon className="w-5 h-5 sm:w-4 sm:h-4" />
                  </div>
                  
                  <div className="ml-3 flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <h4 className="text-sm sm:text-sm font-medium text-gray-900 dark:text-white truncate pr-2 leading-relaxed">
                        {conversation.title}
                      </h4>
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                        <Clock className="w-3 h-3 mr-1" />
                        <span className="hidden sm:inline">{formatTimeAgo(conversation.lastMessageAt)}</span>
                        <span className="sm:hidden">{formatTimeAgo(conversation.lastMessageAt).replace(' ago', '')}</span>
                      </div>
                    </div>
                    <p className="text-xs sm:text-xs text-gray-600 dark:text-gray-300 truncate leading-relaxed">
                      {conversation.lastMessage || "No messages yet"}
                    </p>
                    
                    {/* Mobile: Additional conversation metadata */}
                    <div className="flex items-center justify-between mt-2 sm:hidden">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {conversation.messageCount || 0} messages
                      </span>
                      {isActive && (
                        <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                          Current
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-32 text-gray-500 dark:text-gray-400 px-4">
            <MessageSquare className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-sm text-center">
              {searchQuery ? 'No conversations found' : 'No conversations yet'}
            </p>
            {!searchQuery && (
              <p className="text-xs text-center mt-1 text-gray-400 dark:text-gray-500">
                Start a conversation to see it here
              </p>
            )}
          </div>
        )}
      </div>

      {/* Clear History Button - Mobile Optimized */}
      {conversations.length > 0 && (
        <div className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearHistory}
            className="w-full text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 h-10 sm:h-9 rounded-lg"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            <span className="text-sm">Clear All History</span>
          </Button>
        </div>
      )}
    </div>
  );
}